// ImageSlot — deliberate photo containers for the approved mockup crops.
// Founder-supplied photography is wired below as optimized web copies
// (originals kept untouched in ./assets/originals). Slots without art yet
// render a clearly temporary editorial placeholder.
import archImg from '../assets/arch-modern-terrace-golden-hour.jpg';
import growImg from '../assets/grow-thoughtful-designer-sunset.jpg';
import aboutImg from '../assets/about-tree-modern-interior.jpg';
import clinicImg from '../assets/clinic-moody-meeting-workspace.jpg';
import clinicPageImg from '../assets/clinicpage-journaling-minimal-desk.jpg';
import deliveryImg from '../assets/delivery-collaborative-planning-table.jpg';
import knowledgeImg from '../assets/knowledge-warm-workspace-study.jpg';
import marketingImg from '../assets/marketing-creative-moodboard.jpg';
import optimizeImg from '../assets/optimize-minimalist-warm-workspace.jpg';
import salesImg from '../assets/sales-moody-kanban-laptop.jpg';
import simplifyImg from '../assets/simplify-minimalist-writing-desk.jpg';

// Founder art actually installed. Kept beside the slot metadata so every
// usage of a slotKey (desktop, mobile compact, fill band) shares one asset,
// one conceptual alt text and one focal point. EN/ID render the same art.
export const SLOT_ART = {
  grow: {
    src: growImg,
    imgClassName: 'object-[center_45%]',
    alt: 'Conceptual editorial still — an operator seen from behind working across two monitors in warm light; staged scene, not a Navartha employee',
  },
  arch: {
    src: archImg,
    imgClassName: 'object-center',
    alt: 'Conceptual editorial architecture — a travertine volume with dark fins on a dusk terrace; not a Navartha location',
  },
  about: {
    src: aboutImg,
    imgClassName: 'object-[center_55%]',
    alt: 'Conceptual editorial still — a young tree in a travertine planter inside a quiet modern interior',
  },
  clinic: {
    src: clinicImg,
    imgClassName: 'object-[center_60%]',
    alt: 'Conceptual editorial still — a sunlit meeting table with notebooks and ceramic cups in deep shadow; not a Navartha office',
  },
  clinicPage: {
    src: clinicPageImg,
    imgClassName: 'object-center',
    alt: 'Conceptual editorial still — a hand writing notes beside a laptop and a glass of water; staged scene, not a client engagement',
  },
  delivery: {
    src: deliveryImg,
    imgClassName: 'object-center',
    alt: 'Conceptual editorial still — hands arranging blank planning cards across a timber table; staged scene, not a client engagement',
  },
  knowledge: {
    src: knowledgeImg,
    imgClassName: 'object-center',
    alt: 'Conceptual editorial still — notes taken in open notebooks beside a laptop; staged scene, not a client engagement',
  },
  marketing: {
    src: marketingImg,
    imgClassName: 'object-center',
    alt: 'Conceptual editorial still — hands arranging printed moodboard sheets beside a laptop; staged scene, not a client engagement',
  },
  optimize: {
    src: optimizeImg,
    imgClassName: 'object-[center_58%]',
    alt: 'Conceptual editorial still — hands reviewing printed process documents on a walnut desk; staged scene, not a client engagement',
  },
  sales: {
    src: salesImg,
    imgClassName: 'object-center',
    alt: 'Conceptual editorial still — a pipeline board open on a laptop among working notebooks; staged scene, not a client engagement',
  },
  simplify: {
    src: simplifyImg,
    imgClassName: 'object-[center_62%]',
    alt: 'Conceptual editorial still — a blank notebook and a single pen on a minimal sunlit desk',
  },
};

export const IMG_SLOTS = {
  hero: {
    id: 'hero', index: '01', ratio: '4 / 5', ratioClass: '', tone: 'light',
    title: 'Hero — architectural office interior at dusk',
    note: 'Floor-to-ceiling glass, warm directional light, one person working at a table, city skyline. No faces required; no text or logos in frame.',
  },
  grow: {
    id: 'grow', index: '02', ratio: '2 / 1', ratioClass: 'aspect-[4/3] xl:aspect-[2/1]', tone: 'light',
    title: 'Outcomes / Grow — focused work across monitors, moody',
    note: 'Dark, directional-light workplace detail. Person at screens, mid-work, no direct eye contact.',
  },
  optimize: {
    id: 'optimize', index: '03', ratio: '2 / 1', ratioClass: 'aspect-[4/3] xl:aspect-[2/1]', tone: 'light',
    title: 'Outcomes / Optimize — operations desk detail',
    note: 'Dark still-life: documents, dashboards, hands in motion. Real tools of operations.',
  },
  simplify: {
    id: 'simplify', index: '04', ratio: '2 / 1', ratioClass: 'aspect-[4/3] xl:aspect-[2/1]', tone: 'light',
    title: 'Outcomes / Simplify — minimal desk, reduction',
    note: 'Dark minimal composition: single notebook or laptop, quiet negative space.',
  },
  sales: {
    id: 'sales', index: '05', ratio: '4 / 3', ratioClass: 'aspect-[4/3]', tone: 'light',
    title: 'Value 01 — sales & lead operations',
    note: 'Candid pipeline/CRM work: board, screen with pipeline, notebook. No handshakes.',
  },
  marketing: {
    id: 'marketing', index: '06', ratio: '4 / 3', ratioClass: 'aspect-[4/3]', tone: 'light',
    title: 'Value 02 — marketing & content operations',
    note: 'Content production: drafts on screen, review pass, editorial tools.',
  },
  delivery: {
    id: 'delivery', index: '07', ratio: '4 / 3', ratioClass: 'aspect-[4/3]', tone: 'light',
    title: 'Value 03 — client & delivery operations',
    note: 'Workshop or planning table: whiteboard, project documents, handoff moment.',
  },
  knowledge: {
    id: 'knowledge', index: '08', ratio: '4 / 3', ratioClass: 'aspect-[4/3]', tone: 'light',
    title: 'Value 04 — internal knowledge & repetitive work',
    note: 'Laptop close-up, search or notes in motion. Deliberate crop, directional light.',
  },
  arch: {
    id: 'arch', index: '09', ratio: '21 / 9', ratioClass: '', tone: 'dark',
    title: 'Passage transition — dark architectural facade',
    note: 'Wide architectural crop, dusk, structure and shadow. No people necessary.',
  },
  about: {
    id: 'about', index: '10', ratio: '4 / 5', ratioClass: 'aspect-[4/5]', tone: 'dark',
    title: 'About — modern architecture with natural element',
    note: 'Building facade or court with a tree / natural material, calm dusk light.',
  },
  clinic: {
    id: 'clinic', index: '11', ratio: '4 / 5', ratioClass: 'aspect-[4/5]', tone: 'dark',
    title: 'Business Clinic — moody office, deep shadow',
    note: 'Warm-dark interior, single figure in thought, strong shadow direction.',
  },
  clinicPage: {
    id: 'clinicPage', index: '12', ratio: '4 / 3', ratioClass: 'aspect-[4/3]', tone: 'light',
    title: 'Clinic page — person writing notes at desk',
    note: 'Warm directional light, candid note-taking, editorial crop.',
  },
};

export default function ImageSlot({ slotKey, src, alt, fill = false, compact = false, className = '', imgClassName = '' }) {
  const slot = IMG_SLOTS[slotKey];
  const dark = slot.tone === 'dark';
  const art = SLOT_ART[slotKey];
  const resolvedSrc = src || art?.src;
  const resolvedAlt = alt || art?.alt || slot.title;
  const focal = `${art?.imgClassName || ''} ${imgClassName}`.trim();
  return (
    <div
      role="img"
      aria-label={resolvedSrc ? resolvedAlt : `Image placeholder: ${slot.title}`}
      data-testid={`image-slot-${slotKey}`}
      className={`relative overflow-hidden ${fill ? '' : slot.ratioClass} ${className}`}
      style={fill ? { height: '100%', width: '100%' } : undefined}
    >
      {resolvedSrc ? (
        <img
          src={resolvedSrc}
          alt={resolvedAlt}
          className={`h-full w-full object-cover ${focal}`}
          loading={slotKey === 'hero' ? 'eager' : 'lazy'}
          fetchpriority={slotKey === 'hero' ? 'high' : undefined}
        />
      ) : compact ? (
        <>
          <div className={`absolute inset-0 ${dark ? 'bg-[#1B2430]' : 'bg-[#E9E6DB]'}`} />
          <div className={`absolute left-2 top-2 h-2 w-2 border-l border-t ${dark ? 'border-bone/40' : 'border-midnight/40'}`} />
          <div className={`absolute right-2 top-2 h-2 w-2 border-r border-t ${dark ? 'border-bone/40' : 'border-midnight/40'}`} />
          <div className={`absolute bottom-2 left-2 h-2 w-2 border-b border-l ${dark ? 'border-bone/40' : 'border-midnight/40'}`} />
          <div className={`absolute bottom-2 right-2 h-2 w-2 border-b border-r ${dark ? 'border-bone/40' : 'border-midnight/40'}`} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`eyebrow ${dark ? 'text-bone/50' : 'text-midnight/45'}`}>Photo {slot.index}</span>
          </div>
        </>
      ) : (
        <>
          <div className={`absolute inset-0 ${slot.tone === 'dark' ? 'bg-[#1B2430]' : 'bg-[#E9E6DB]'}`} />
          <div className={`absolute left-4 top-4 h-3 w-3 border-l border-t ${slot.tone === 'dark' ? 'border-bone/40' : 'border-midnight/40'}`} />
          <div className={`absolute right-4 top-4 h-3 w-3 border-r border-t ${slot.tone === 'dark' ? 'border-bone/40' : 'border-midnight/40'}`} />
          <div className={`absolute bottom-4 left-4 h-3 w-3 border-b border-l ${slot.tone === 'dark' ? 'border-bone/40' : 'border-midnight/40'}`} />
          <div className={`absolute bottom-4 right-4 h-3 w-3 border-b border-r ${slot.tone === 'dark' ? 'border-bone/40' : 'border-midnight/40'}`} />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
            <div className={`h-px w-8 ${slot.tone === 'dark' ? 'bg-bone/30' : 'bg-midnight/30'}`} />
            <span className={`eyebrow ${slot.tone === 'dark' ? 'text-bone/50' : 'text-midnight/45'}`}>Photo {slot.index}</span>
            <span className={`max-w-[280px] text-[10px] uppercase leading-relaxed tracking-[0.18em] ${slot.tone === 'dark' ? 'text-bone/50' : 'text-midnight/45'}`}>
              {slot.title}
            </span>
            <span className="mt-1 text-[9px] uppercase tracking-[0.22em] text-vermilion">
              Awaiting official art — supplied separately
            </span>
            <div className={`h-px w-8 ${slot.tone === 'dark' ? 'bg-bone/30' : 'bg-midnight/30'}`} />
          </div>
        </>
      )}
    </div>
  );
}
